/**
 * Size Guide Web Component
 *
 * This web component creates a modal drawer to display size guide information for a product.
 * It handles the rendering of size guide data, including title, description, image, and table,
 * in a reusable drawer that can be triggered from any product card or page.
 *
 * Features:
 * - Renders a shared overlay and drawer for all size guides
 * - Dynamically updates drawer content based on the clicked button's data
 * - Uses an SVG icon for the close button
 * - Supports smooth scrolling within the drawer
 * - Disables body scrolling while the drawer is active
 * - Closes the drawer on ESC key press
 * - Automatically focuses the drawer wrapper when the drawer opens
 * - Supports opening the drawer via a link in the product description
 */
class SizeGuide extends HTMLElement {
  // Static variables to store the shared overlay, drawer, and close icon SVG
  static overlay = null;
  static drawer = null;
  static closeIconSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M6 18L18 6M6 6L18 18" stroke="#191919" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  constructor() {
    super();
    this.setupDrawer();
  }

  // Initialize the button and set up the drawer
  setupDrawer() {
    const button = this.querySelector('.size-guide-button');
    if (!button) return;

    // Store the button for focus management
    this.openButton = button;

    this.initializeOverlayAndDrawer();

    // Handle clicks on the size-guide-button
    button.addEventListener('click', (event) => {
      this.lastTriggerElement = event.currentTarget;
      this.handleButtonClick();
    });

    // Handle clicks on size-guide-opener-link elements in the product description
    const openerLinks = document.querySelectorAll('.size-guide-opener-link');
    openerLinks.forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastTriggerElement = event.currentTarget;
        this.handleLinkClick();
      });
    });
  }

  // Create or reuse the overlay and drawer elements
  initializeOverlayAndDrawer() {
    if (!SizeGuide.overlay) {
      SizeGuide.overlay = document.createElement('div');
      SizeGuide.overlay.classList.add('size-guide-overlay');
      document.body.appendChild(SizeGuide.overlay);
    }

    if (!SizeGuide.drawer) {
      SizeGuide.drawer = document.createElement('div');
      SizeGuide.drawer.classList.add('size-guide-drawer');
      document.body.appendChild(SizeGuide.drawer);
    }
  }

  // Handle button click to open the drawer
  handleButtonClick() {
    const sizeGuideData = this.querySelector('.size-guide-button').getAttribute('data-size-guide') || '{}';
    const { title, description, image, table } = JSON.parse(sizeGuideData);

    const tableHTML = this.generateTableHTML(table);
    this.updateDrawerContent(title, description, image, tableHTML);
    this.openDrawer();
    this.setupCloseEvents();
  }

  // Handle link click to open the drawer
  handleLinkClick() {
    const button = document.querySelector('.size-guide-button');
    if (!button) return;

    const sizeGuideData = button.getAttribute('data-size-guide') || '{}';
    const { title, description, image, table } = JSON.parse(sizeGuideData);

    const tableHTML = this.generateTableHTML(table);
    this.updateDrawerContent(title, description, image, tableHTML);
    this.openDrawer();
    this.setupCloseEvents();
  }

  // Generate HTML table from CSV content
  generateTableHTML(table) {
    let tableHTML = '';
    if (table) {
      const rows = table.split('\n')
        .map(row => row.trim())
        .filter(row => row.length > 0)
        .map(row => row.split(',').map(cell => cell.trim()));
      
      if (rows.length > 0) {
        tableHTML = '<table>';
        rows.forEach((row, index) => {
          const tag = index === 0 ? 'th' : 'td';
          tableHTML += '<tr>';
          row.forEach(cell => {
            tableHTML += `<${tag}>${cell || ''}</${tag}>`;
          });
          tableHTML += '</tr>';
        });
        tableHTML += '</table>';
      }
    }
    return tableHTML;
  }

  // Update the drawer content with the current size guide data
  updateDrawerContent(title, description, image, tableHTML) {
    SizeGuide.drawer.innerHTML = `
      <div class="size-guide-drawer__header">
        <h2 class="size-guide-drawer__heading">${title || ''}</h2>
        <button 
          class="size-guide-drawer__close"
          type="button"
          aria-label="${window.accessibilityStrings.close}"
        >
          ${SizeGuide.closeIconSVG}
        </button>
      </div>
      <div class="size-guide-drawer__content">
        <p class="size-guide-drawer__description">${description || ''}</p>
        ${image ? `
          <div class="size-guide-drawer__image">
            <img src="${image}" alt="${title || ''}">
          </div>
        ` : ''}            
        <div class="size-guide-drawer__table size-guide-table">${tableHTML}</div>
      </div>
    `;
  }

  // Close any open drawer
  closeOpenDrawers() {
    if (SizeGuide.drawer.classList.contains('open')) {
      SizeGuide.drawer.classList.remove('open');
      SizeGuide.overlay.classList.remove('active');
      document.documentElement.classList.remove('no-scroll');
      document.removeEventListener('keydown', this.handleEscKey);
      SizeGuide.drawer.removeAttribute('tabindex');
      // Return focus to the element that triggered the drawer opening
      if (this.lastTriggerElement) {
        setTimeout(() => {
          this.lastTriggerElement.focus();
          console.log('Focus set to last trigger element:', document.activeElement);
        }, 10);
      } else if (this.openButton) {
        // Fallback to openButton if lastTriggerElement is not set
        setTimeout(() => {
          this.openButton.focus();
          console.log('Focus set to openButton:', document.activeElement);
        }, 10);
      }
    }
  }

  // Handle ESC key press to close the drawer
  handleEscKey(event) {
    if (event.key === 'Escape') {
      this.closeOpenDrawers();
    }
  }

  // Open the drawer, disable body scrolling, and set up focus
  openDrawer() {
    this.closeOpenDrawers();
    SizeGuide.overlay.classList.add('active');
    document.documentElement.classList.add('no-scroll');

    // Add ESC key listener
    document.addEventListener('keydown', this.handleEscKey.bind(this));

    // Ensure the drawer is focusable when opened
    SizeGuide.drawer.setAttribute('tabindex', '0');

    setTimeout(() => {
      SizeGuide.drawer.classList.add('open');
      // Focus the drawer after the drawer is opened
      SizeGuide.drawer.focus();
    }, 10);
  }

  // Set up close event listeners for the drawer
  setupCloseEvents() {
    const closeButton = SizeGuide.drawer.querySelector('.size-guide-drawer__close');
    if (closeButton) {
      closeButton.removeEventListener('click', this.closeDrawerHandler);
      this.closeDrawerHandler = () => {
        this.closeOpenDrawers();
      };
      closeButton.addEventListener('click', this.closeDrawerHandler);
    }

    SizeGuide.overlay.removeEventListener('click', this.closeOverlayHandler);
    this.closeOverlayHandler = () => {
      this.closeOpenDrawers();
    };
    SizeGuide.overlay.addEventListener('click', this.closeOverlayHandler);
  }
}

customElements.define('size-guide', SizeGuide);
